import React, { useState, useEffect } from 'react';
import { TourQuestionnaire } from '../../../types';
import { cn } from '../../../lib/utils';
import { UtensilsCrossed, Coffee, Utensils, Check, ChevronRight } from 'lucide-react';

interface Step9_3Props {
  questionnaire: TourQuestionnaire;
  setQuestionnaire: React.Dispatch<React.SetStateAction<TourQuestionnaire>>;
  currentDay: number;
  currentMoment: 'lunch' | 'dinner';
  handleNext: () => void;
  handleBack: () => void;
}

export const Step9_3: React.FC<Step9_3Props> = ({
  questionnaire,
  setQuestionnaire,
  currentDay,
  currentMoment,
  handleNext,
  handleBack,
}) => {
  // Read current selection from dailyCatering for this specific day/moment
  const currentCatering = questionnaire.dailyCatering?.[currentDay];
  const currentType = currentMoment === 'lunch'
    ? (currentCatering?.lunch?.type ?? questionnaire.catering.type)
    : (currentCatering?.eveningMeal?.type ?? null);
  const currentStanding = currentMoment === 'lunch'
    ? (currentCatering?.lunch?.standing ?? questionnaire.catering.standing)
    : (currentCatering?.eveningMeal?.standing ?? undefined);

  const [showForks, setShowForks] = useState(currentType === 'restaurant');

  // Keep showForks in sync if we navigate back to this step for a different day
  useEffect(() => {
    setShowForks(currentType === 'restaurant');
  }, [currentDay, currentMoment]);

  const types = [
    { id: 'pique-nique', label: 'Pique-nique', icon: UtensilsCrossed, desc: 'Repas en plein air' },
    { id: 'snack', label: 'Snack / Rapide', icon: Coffee, desc: 'Simple et efficace' },
    { id: 'restaurant', label: 'Restaurant', icon: Utensils, desc: 'Table traditionnelle' },
  ];

  const handleTypeSelect = (typeId: string) => {
    setQuestionnaire(prev => {
      const dailyCatering = { ...(prev.dailyCatering || {}) };
      const existing = dailyCatering[currentDay] || {
        lunch: { type: prev.catering.type, standing: prev.catering.standing },
        eveningMealIncluded: false,
      };

      const updated = { ...existing };
      if (currentMoment === 'lunch') {
        updated.lunch = { ...updated.lunch, type: typeId as any };
      } else {
        updated.eveningMeal = { ...(updated.eveningMeal || { type: 'restaurant', standing: 1 }), type: typeId as any };
        updated.eveningMealIncluded = true;
      }
      dailyCatering[currentDay] = updated;

      // Also keep global catering in sync (used as fallback in circuitEngine)
      return {
        ...prev,
        catering: { ...prev.catering, type: typeId as any },
        dailyCatering,
      };
    });
    setShowForks(typeId === 'restaurant');
  };

  const handleForkSelect = (forks: 1 | 2 | 3) => {
    setQuestionnaire(prev => {
      const dailyCatering = { ...(prev.dailyCatering || {}) };
      const existing = dailyCatering[currentDay] || {
        lunch: { type: prev.catering.type, standing: prev.catering.standing },
        eveningMealIncluded: false,
      };

      const updated = { ...existing };
      if (currentMoment === 'lunch') {
        updated.lunch = { ...updated.lunch, standing: forks };
      } else {
        updated.eveningMeal = { ...(updated.eveningMeal || { type: 'restaurant', standing: 1 }), standing: forks };
      }
      dailyCatering[currentDay] = updated;

      return {
        ...prev,
        catering: { ...prev.catering, standing: forks },
        dailyCatering,
      };
    });
  };

  const isValid = currentType !== null && currentType !== undefined &&
    (currentType !== 'restaurant' || (currentStanding !== undefined && currentStanding !== null));

  return (
    <div className="flex flex-col h-[100dvh] p-0 overflow-hidden bg-zinc-950">
      <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar pb-32">
        <div className="space-y-2">
          <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
            Restauration {currentMoment === 'lunch' ? 'Midi' : 'Soir'} — Jour {currentDay}
          </h2>
          <p className="text-zinc-400">
            Quel type de pause {currentMoment === 'lunch' ? 'déjeuner' : 'dîner'} préférez-vous ?
          </p>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3">
            {types.map(type => (
              <button
                key={type.id}
                onClick={() => handleTypeSelect(type.id)}
                className={cn(
                  "p-3.5 rounded-xl border flex items-center gap-4 transition-all text-left relative group",
                  currentType === type.id
                    ? "bg-emerald-500/10 border-emerald-500 text-white"
                    : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700"
                )}
              >
                <div className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center transition-colors",
                  currentType === type.id ? "bg-emerald-500 text-white" : "bg-zinc-800 text-zinc-500"
                )}>
                  <type.icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <span className="block text-sm font-semibold">{type.label}</span>
                  <span className="block text-[10px] opacity-60">{type.desc}</span>
                </div>
                {currentType === type.id && (
                  <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>

          {showForks && (
            <div className="space-y-4 pt-4 animate-in fade-in slide-in-from-top-4 duration-300">
              <div className="flex items-center gap-2">
                <div className="h-px flex-1 bg-white/5" />
                <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Standing souhaité</span>
                <div className="h-px flex-1 bg-white/5" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                {([1, 2, 3] as const).map(forks => (
                  <button
                    key={forks}
                    onClick={() => handleForkSelect(forks)}
                    className={cn(
                      "p-3 rounded-xl border flex flex-col items-center gap-2 transition-all group",
                      currentStanding === forks
                        ? "bg-amber-500/10 border-amber-500 text-white"
                        : "bg-zinc-900 border-zinc-800 text-zinc-500 hover:border-zinc-700"
                    )}
                  >
                    <div className="flex gap-0.5">
                      {[...Array(forks)].map((_, i) => (
                        <Utensils key={i} className={cn(
                          "w-3.5 h-3.5",
                          currentStanding === forks ? "text-amber-400" : "text-zinc-600 group-hover:text-zinc-400"
                        )} />
                      ))}
                    </div>
                    <span className="text-[9px] font-semibold uppercase tracking-wider">
                      {forks === 1 ? 'Standard' : forks === 2 ? 'Qualité' : 'Gastro'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-30 bg-zinc-950/95 backdrop-blur-xl border-t border-white/10">
        <div className="max-w-7xl mx-auto p-4 md:p-6 flex gap-3">
          <button
            onClick={handleBack}
            className="flex-1 py-3 bg-zinc-900 text-zinc-400 rounded-xl font-semibold text-sm hover:bg-zinc-800 border border-zinc-800 transition-colors"
          >
            Retour
          </button>
          <button
            onClick={handleNext}
            disabled={!isValid}
            className="flex-[2] py-3 bg-emerald-500 text-white rounded-xl font-semibold text-sm hover:bg-emerald-600 transition-all shadow-[0_0_15px_rgba(16,185,129,0.3)] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continuer
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
